import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { useMasterData } from '../../hooks/useMasterData';
import { CourseSelect, SessionSelect } from '../../components/MasterDataSelect';
import CareerCoachField from '../../components/CareerCoachField';
import SignaturePad from './SignaturePad';
import { toast, Toaster } from 'react-hot-toast';
import { API_BASE_URL, getAuthHeaders } from '../../utils/apiConfig';

const Container = styled.div`
  padding: 10px;
  max-width: 1400px;
  margin: 0 auto;
`;

const Title = styled.h1`
  color: #2c3e50;
  margin-bottom: 30px;
  font-size: 1.5rem;
`;

const Form = styled.form`
  background: white;
  padding: 30px;
  border-radius: 15px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.08);
`;

const Section = styled.div`
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 1px solid #e9ecef;

  &:last-child {
    border-bottom: none;
  }
`;

const SectionTitle = styled.h3`
  color: #2c3e50;
  margin-bottom: 20px;
  font-size: 1.3rem;
  font-weight: 600;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
`;

const Label = styled.label`
  margin-bottom: 8px;
  color: #333;
  font-weight: 500;
`;

const Input = styled.input`
  padding: 12px 16px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 16px;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: rgb(51, 66, 147);;
  }
`;

const Select = styled.select`
  padding: 12px 16px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 16px;
  transition: border-color 0.3s ease;
  background: white;

  &:focus {
    outline: none;
    border-color: rgb(51, 66, 147);;
  }
`;

const TextArea = styled.textarea`
  padding: 12px 16px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 16px;
  min-height: 100px;
  transition: border-color 0.3s ease;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: rgb(51, 66, 147);;
  }
`;

const FileInput = styled.input`
  padding: 12px 16px;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  font-size: 16px;
  transition: border-color 0.3s ease;

  &:focus {
    outline: none;
    border-color: rgb(51, 66, 147);;
  }
`;

const PhoneInputContainer = styled.div`
  display: flex;
  align-items: center;
  border: 2px solid #e1e5e9;
  border-radius: 8px;
  overflow: hidden;
  transition: border-color 0.3s ease;

  &:focus-within {
    border-color: rgb(51, 66, 147);;
  }
`;

const CountryCode = styled.div`
  background: #f8f9fa;
  padding: 12px 16px;
  border-right: 1px solid #e1e5e9;
  font-size: 16px;
  color: #495057;
`;

const PhoneInput = styled.input`
  flex: 1;
  padding: 12px 16px;
  border: none;
  font-size: 16px;
  outline: none;
`;

const PhoneIcon = styled.span`
  padding: 0 16px;
  color: #333;
  font-size: 1.3em;
`;

const RequiredStar = styled.span`
  color: #dc2626;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 15px;
  justify-content: flex-end;
  margin-top: 30px;
`;

const Button = styled.button`
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 120px;

  &:disabled {
    background-color: #6c757d;
    cursor: not-allowed;
    opacity: 0.6;
  }

  &:disabled:hover {
    background-color: #6c757d;
    transform: none;
  }
`;

const PrimaryButton = styled(Button)`
  background: rgb(51, 66, 147);;
  color: white;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(102, 126, 234, 0.3);
  }
`;

const SecondaryButton = styled(Button)`
  background: #e9ecef;
  color: #495057;

  &:hover {
    background: #dee2e6;
  }
`;

const ErrorSummary = styled.div`
  background-color: #fff3f3;
  border: 1px solid #ffcdd2;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;
  color: #d32f2f;
`;

const ErrorTitle = styled.h4`
  color: #d32f2f;
  margin: 0 0 12px 0;
  font-size: 1.1rem;
  font-weight: 600;
`;

const ErrorList = styled.ul`
  margin: 0;
  padding-left: 20px;
  list-style-type: disc;
`;

const ErrorItem = styled.li`
  margin-bottom: 4px;
  font-size: 0.9rem;
`;

const SuccessMessage = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  background-color: #4caf50;
  color: white;
  padding: 16px 24px;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  display: flex;
  align-items: center;
  gap: 12px;
  animation: slideIn 0.3s ease-out;

  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;

const AddStudentApplication = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { courses, sessions, semesters, isLoading: masterDataLoading } = useMasterData();
    const [formErrors, setFormErrors] = useState({});
    const [backendError, setBackendError] = useState('');
    const [isFormValid, setIsFormValid] = useState(false);
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        dateOfBirth: '',
        address: '',
        course: '',
        studentNotes: [{ note: '', enteredBy: '', date: '' }],
        session: '',
        dlUpload: null,
        socialSecurityNumber: '',
        workForceCenter: '',
        socialSecurityUpload: null,
        emergencyContactInfo: '',
        emergencyContactPhone: '',
        otherEmergencyContact: '',
        notes: '',
        phone: '',
        dateOfJoining: '',
        addressLine1: '',
        city: '',
        state: '',
        postalCode: '',
        country: '',
        gender: '',
        nationality: '',
        photo: null,
        coursePref1: '',
        daysPref1: '',
        locationPref1: '',
        coursePref2: '',
        daysPref2: '',
        locationPref2: '',
        attendedInfoSession: false,
        infoSessionLocation: '',
        additionalComments: '',
        signature: '',
        nameCapitalization: false,
        loginId: '',
        password: ''
    });

    // Function to check if all required fields are filled
    const checkFormValidity = () => {
        const requiredFields = [
            'firstName', 'lastName', 'email', 'phone', 'dateOfBirth',
            'loginId', 'password', 'course', 'session', 'gender',
            'socialSecurityNumber', 'coursePref1', 'daysPref1', 'locationPref1', 'workForceCenter',
            'nationality'
        ];
        
        let allFieldsFilled = true;
        
        // Check required text fields
        requiredFields.forEach(field => {
            if (!formData[field] || formData[field].toString().trim() === '') {
                allFieldsFilled = false;
            }
        });
        
        // Check gender radio buttons
        if (!formData.gender) {
            allFieldsFilled = false;
        }
        
        // Check signature
        if (!formData.signature) {
            allFieldsFilled = false;
        }
        
        // Password length check
        if (!formData.password || formData.password.length < 6) {
            allFieldsFilled = false;
        }
        
        setIsFormValid(allFieldsFilled);
    };

    // Check form validity whenever formData changes
    useEffect(() => {
        checkFormValidity();
    }, [formData]);

    const validateForm = () => {
        const errors = {};
        const requiredFields = [
            'firstName', 'lastName', 'email', 'phone', 'dateOfBirth',
            'loginId', 'password', 'course', 'session', 'gender',
            'socialSecurityNumber', 'coursePref1', 'daysPref1', 'locationPref1', 'workForceCenter',
            'nationality'
        ];

        // Check required fields
        requiredFields.forEach(field => {
            if (!formData[field]) {
                errors[field] = 'This field is required';
            }
        });

        // Check signature
        if (!formData.signature) {
            errors.signature = 'Signature is required';
        }

        // Email validation
        if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
            errors.email = 'Please enter a valid email address';
        }

        // Contact number validation - must be 10 digits
        if (formData.phone) {
            const phoneDigits = formData.phone.replace(/\D/g, '');
            if (phoneDigits.length !== 10) {
                errors.phone = 'Contact number must be 10 digits';
            }
        }

        // Password validation - must be 6+ characters
        if (formData.password && formData.password.length < 6) {
            errors.password = 'Password must be at least 6 characters long';
        }

        // Social Security Number validation - must be 4 digits
        if (formData.socialSecurityNumber) {
            const ssnDigits = formData.socialSecurityNumber.replace(/\D/g, '');
            if (ssnDigits.length !== 4) {
                errors.socialSecurityNumber = 'SSN must be 4 digits';
            }
        }

        // Gender validation
        if (formData.gender && !['Male', 'Female', 'Other'].includes(formData.gender)) {
            errors.gender = 'Please select a valid gender';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value, type, files } = e.target;
        // Format contact number as user types
        if (name === 'phone') {
            const phoneDigits = value.replace(/\D/g, '');
            const formattedPhone = phoneDigits.slice(0, 10).replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
            setFormData(prev => ({
                ...prev,
                [name]: formattedPhone
            }));
        }
        // Format emergency contact phone as user types
        else if (name === 'emergencyContactPhone') {
            const phoneDigits = value.replace(/\D/g, '');
            const formattedPhone = phoneDigits.slice(0, 10).replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
            setFormData(prev => ({
                ...prev,
                [name]: formattedPhone
            }));
        }
        // Format SSN as user types - only last 4 digits
        else if (name === 'socialSecurityNumber') {
            const ssnDigits = value.replace(/\D/g, '');
            const formattedSSN = ssnDigits.slice(0, 4);
            setFormData(prev => ({
                ...prev,
                [name]: formattedSSN
            }));
        }
        else if (type === 'file') {
            // Validate file type and size
            const file = files[0];
            if (file) {
                const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'];
                const maxSize = 5 * 1024 * 1024; // 5MB

                if (!allowedTypes.includes(file.type)) {
                    setFormErrors(prev => ({
                        ...prev,
                        [name]: 'Please upload a valid file (JPEG, PNG, GIF, or PDF)'
                    }));
                    return;
                }

                if (file.size > maxSize) {
                    setFormErrors(prev => ({
                        ...prev,
                        [name]: 'File size should not exceed 5MB'
                    }));
                    return;
                }

                setFormData(prev => ({
                    ...prev,
                    [name]: file
                }));
            }
        }
        else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }

        // Clear error when field is edited
        if (formErrors[name]) {
            setFormErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    // Notes handling functions
    const addNote = () => {
        setFormData(prev => ({
            ...prev,
            studentNotes: [...prev.studentNotes, { note: '', enteredBy: '', date: '' }]
        }));
    };

    const removeNote = (index) => {
        setFormData(prev => ({
            ...prev,
            studentNotes: prev.studentNotes.filter((_, i) => i !== index)
        }));
    };

    const handleNotesChange = (index, field, value) => {
        setFormData(prev => ({
            ...prev,
            studentNotes: prev.studentNotes.map((note, i) =>
                i === index ? { ...note, [field]: value } : note
            )
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Clear any previous backend error
        setBackendError('');

        if (!validateForm()) {
            // Scroll to error summary
            const errorSummary = document.getElementById('error-summary');
            if (errorSummary) {
                errorSummary.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
            return;
        }

        // Disable submit button and show loading state
        const submitButton = document.querySelector('button[type="submit"]');
        const originalText = submitButton.textContent;
        submitButton.disabled = true;
        submitButton.textContent = 'Submitting...';

        try {
            // Create FormData object
            const formDataToSend = new FormData();

            // Handle studentNotes array specially
            if (formData.studentNotes && Array.isArray(formData.studentNotes)) {
                // Convert notes array to a format the backend can understand
                formData.studentNotes.forEach((note, index) => {
                    if (note.note) {
                        formDataToSend.append(`studentNotes[${index}][note]`, note.note);
                        formDataToSend.append(`studentNotes[${index}][enteredBy]`, note.enteredBy || '');
                        formDataToSend.append(`studentNotes[${index}][date]`, note.date || '');
                    }
                });
            }

            // Append all form fields to FormData
            Object.keys(formData).forEach(key => {
                const value = formData[key];

                // Skip studentNotes as it's handled separately
                if (key === 'studentNotes') {
                    return;
                }

                // Skip null, undefined, and empty string values
                if (value !== null && value !== undefined && value !== '') {
                    // Handle boolean values
                    if (typeof value === 'boolean') {
                        formDataToSend.append(key, value.toString());
                    }
                    // Handle File objects
                    else if (value instanceof File) {
                        formDataToSend.append(key, value);
                    }
                    // Handle all other values as strings
                    else {
                        formDataToSend.append(key, value.toString());
                    }
                }
            });

         
            for (let pair of formDataToSend.entries()) {
            }

            // Send the request to applications endpoint
            const response = await fetch(`${API_BASE_URL}/applications`, {
                method: 'POST',
                headers: { ...getAuthHeaders() },
                body: formDataToSend
            });

            const result = await response.json();

            if (result.success) {
                // Show success message
                toast.custom((t) => (
                    <SuccessMessage>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="currentColor" />
                        </svg>
                        Student application submitted successfully!
                    </SuccessMessage>
                ), {
                    duration: 4000,
                    position: 'top-right',
                });
                handleReset();
            } else {
                // Handle backend validation errors
                if (result.errors && Array.isArray(result.errors)) {
                    const errorMessages = result.errors.map(error => error.msg).join(', ');
                    setBackendError(errorMessages);
                } else {
                    setBackendError(result.message || 'Failed to submit application. Please try again.');
                }
                // Scroll to error summary
                const errorSummary = document.getElementById('error-summary');
                if (errorSummary) {
                    errorSummary.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
            }
        } catch (error) {
            console.error('Error submitting form:', error);
            setBackendError('Failed to submit application. Please try again.');
            // Scroll to error summary
            const errorSummary = document.getElementById('error-summary');
            if (errorSummary) {
                errorSummary.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        } finally {
            // Re-enable submit button and restore original text
            submitButton.disabled = false;
            submitButton.textContent = originalText;
        }
    };

    const handleReset = () => {
        setFormData({
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            dateOfBirth: '',
            address: '',
            course: '',
            studentNotes: [{ note: '', enteredBy: '', date: '' }],
            session: '',
            dlUpload: null,
            socialSecurityNumber: '',
            workForceCenter: '',
            socialSecurityUpload: null,
            emergencyContactInfo: '',
            emergencyContactPhone: '',
            otherEmergencyContact: '',
            notes: '',
            phone: '',
            dateOfJoining: '',
            addressLine1: '',
            city: '',
            state: '',
            postalCode: '',
            country: '',
            gender: '',
            nationality: '',
            photo: null,
            coursePref1: '',
            daysPref1: '',
            locationPref1: '',
            coursePref2: '',
            daysPref2: '',
            locationPref2: '',
            attendedInfoSession: '',
            infoSessionLocation: '',
            additionalComments: '',
            signature: '',
            nameCapitalization: false,
            loginId: '',
            password: ''
        });
        setBackendError(''); // Clear backend error on reset
    };

    const handleFileChange = (e) => {
        const { name, files } = e.target;
        const file = files[0];

        if (file) {
            // Validate file type
            const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
            if (!allowedTypes.includes(file.type)) {
                setFormErrors(prev => ({
                    ...prev,
                    [name]: 'Please upload a valid file (JPEG, PNG, GIF, WebP, or PDF)'
                }));
                e.target.value = ''; // Clear the file input
                return;
            }

            // Validate file size (5MB max)
            const maxSize = 5 * 1024 * 1024;
            if (file.size > maxSize) {
                setFormErrors(prev => ({
                    ...prev,
                    [name]: 'File size should not exceed 5MB'
                }));
                e.target.value = ''; // Clear the file input
                return;
            }

            // Update form data with the file
            setFormData(prev => ({
                ...prev,
                [name]: file
            }));

            // Clear any existing error
            if (formErrors[name]) {
                setFormErrors(prev => ({
                    ...prev,
                    [name]: ''
                }));
            }

        }
    };

    // Update getAllErrors to include backend error
    const getAllErrors = () => {
        const errors = [];

        // Add backend error if it exists
        if (backendError) {
            errors.push(backendError);
        }

        // Add form validation errors
        Object.entries(formErrors).forEach(([field, message]) => {
            if (message) {
                // Convert field name to a more readable format
                const readableField = field
                    .replace(/([A-Z])/g, ' $1')
                    .replace(/^./, str => str.toUpperCase())
                    .replace(/([A-Z])/g, match => ' ' + match.toLowerCase());

                errors.push(`${readableField}: ${message}`);
            }
        });
        return errors;
    };

    return (
        <Container>
            <Toaster />
            <Title>Add Student Application</Title>
            <Form onSubmit={handleSubmit}>
                {/* Student Registration Section */}
                <Section>
                    <SectionTitle>Student Registration</SectionTitle>
                    <FormRow style={{ gridTemplateColumns: '1fr 1fr' }}>
                        <FormGroup>
                            <Label>First Name <RequiredStar>*</RequiredStar></Label>
                            <Input
                                name="firstName"
                                value={formData.firstName}
                                onChange={handleChange}
                                required
                                placeholder="Enter first name"
                                error={!!formErrors.firstName}
                            />
                            {formErrors.firstName && <span style={{ color: 'red', fontSize: '0.8rem' }}>{formErrors.firstName}</span>}
                        </FormGroup>
                        <FormGroup>
                            <Label>Last Name <RequiredStar>*</RequiredStar></Label>
                            <Input name="lastName" value={formData.lastName} onChange={handleChange} required placeholder="Enter last name" />
                        </FormGroup>
                    </FormRow>
                    <FormRow style={{ gridTemplateColumns: '1fr 1fr' }}>
                        <FormGroup>
                            <Label>Contact Number <RequiredStar>*</RequiredStar></Label>
                            <PhoneInputContainer>
                                <CountryCode>+1</CountryCode>
                                <PhoneInput
                                name="phone"
                                value={formData.phone}
                                onChange={handleChange}
                                required
                                placeholder="(123) 456-7890"
                                error={!!formErrors.phone}
                            />
                                <PhoneIcon>📞</PhoneIcon>
                            </PhoneInputContainer>
                            {formErrors.phone && (
                                <span style={{ color: 'red', fontSize: '0.8rem', marginTop: '4px' }}>
                                    {formErrors.phone}
                                </span>
                            )}
                        </FormGroup>
                        <FormGroup>
                            <Label>Email Address <RequiredStar>*</RequiredStar></Label>
                            <Input name="email" type="email" value={formData.email} onChange={handleChange} required placeholder="Enter email address" />
                        </FormGroup>
                    </FormRow>
                    <FormRow style={{ gridTemplateColumns: '1fr 1fr' }}>
                        <FormGroup>
                            <Label>Date of Application</Label>
                            <Input name="dateOfJoining" type="date" value={formData.dateOfJoining || ''} onChange={handleChange} placeholder="mm/dd/yyyy" />
                        </FormGroup>
                        <FormGroup>
                            <Label>Date of Birth <RequiredStar>*</RequiredStar></Label>
                            <Input name="dateOfBirth" type="date" value={formData.dateOfBirth || ''} onChange={handleChange} placeholder="mm/dd/yyyy" required />
                        </FormGroup>
                    </FormRow>
                    <FormRow style={{ gridTemplateColumns: '1fr 1fr' }}>

                        <FormGroup>
                            <Label>Address <RequiredStar>*</RequiredStar></Label>
                            <Input name="addressLine1" value={formData.addressLine1 || ''} onChange={handleChange} placeholder="Address Line 1" required />
                            <Input name="city" value={formData.city || ''} onChange={handleChange} placeholder="City/District" style={{ marginTop: 8 }} />
                            <Select name="state" value={formData.state || ''} onChange={handleChange} style={{ marginTop: 8 }}>
                                <option value="">- Select State/Province -</option>
                                <option value="AL">Alabama</option>
                                <option value="AK">Alaska</option>
                                <option value="AZ">Arizona</option>
                                <option value="AR">Arkansas</option>
                                <option value="CA">California</option>
                                <option value="CO">Colorado</option>
                                <option value="CT">Connecticut</option>
                                <option value="DE">Delaware</option>
                                <option value="FL">Florida</option>
                                <option value="GA">Georgia</option>
                                <option value="HI">Hawaii</option>
                                <option value="ID">Idaho</option>
                                <option value="IL">Illinois</option>
                                <option value="IN">Indiana</option>
                                <option value="IA">Iowa</option>
                                <option value="KS">Kansas</option>
                                <option value="KY">Kentucky</option>
                                <option value="LA">Louisiana</option>
                                <option value="ME">Maine</option>
                                <option value="MD">Maryland</option>
                                <option value="MA">Massachusetts</option>
                                <option value="MI">Michigan</option>
                                <option value="MN">Minnesota</option>
                                <option value="MS">Mississippi</option>
                                <option value="MO">Missouri</option>
                                <option value="MT">Montana</option>
                                <option value="NE">Nebraska</option>
                                <option value="NV">Nevada</option>
                                <option value="NH">New Hampshire</option>
                                <option value="NJ">New Jersey</option>
                                <option value="NM">New Mexico</option>
                                <option value="NY">New York</option>
                                <option value="NC">North Carolina</option>
                                <option value="ND">North Dakota</option>
                                <option value="OH">Ohio</option>
                                <option value="OK">Oklahoma</option>
                                <option value="OR">Oregon</option>
                                <option value="PA">Pennsylvania</option>
                                <option value="RI">Rhode Island</option>
                                <option value="SC">South Carolina</option>
                                <option value="SD">South Dakota</option>
                                <option value="TN">Tennessee</option>
                                <option value="TX">Texas</option>
                                <option value="UT">Utah</option>
                                <option value="VT">Vermont</option>
                                <option value="VA">Virginia</option>
                                <option value="WA">Washington</option>
                                <option value="WV">West Virginia</option>
                                <option value="WI">Wisconsin</option>
                                <option value="WY">Wyoming</option>
                            </Select>
                            <Input name="postalCode" value={formData.postalCode || ''} onChange={handleChange} placeholder="Postal Code" style={{ marginTop: 8 }} />
                            <Select name="country" value={formData.country || ''} onChange={handleChange} style={{ marginTop: 8 }}>
                                <option value="">- Select Country -</option>
                                <option value="USA">USA</option>
                                <option value="Canada">Canada</option>
                                <option value="Mexico">Mexico</option>
                                <option value="Others">Others</option>
                            </Select>
                        </FormGroup>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            <FormGroup>
                                <Label>Gender <RequiredStar>*</RequiredStar></Label>
                                <div style={{ display: 'flex', gap: 16 }}>
                                    <label><input type="radio" name="gender" value="Male" checked={formData.gender === 'Male'} onChange={handleChange} /> Male</label>
                                    <label><input type="radio" name="gender" value="Female" checked={formData.gender === 'Female'} onChange={handleChange} /> Female</label>
                                    <label><input type="radio" name="gender" value="Other" checked={formData.gender === 'Other'} onChange={handleChange} /> Other</label>
                                </div>
                            </FormGroup>
                            <FormGroup>
                                <Label>Photo</Label>
                                <FileInput
                                    name="photo"
                                    type="file"
                                    onChange={handleFileChange}
                                    accept=".jpg,.jpeg,.png,.gif,.webp,.pdf"
                                />
                                {formErrors.photo && (
                                    <span style={{ color: 'red', fontSize: '0.8rem' }}>{formErrors.photo}</span>
                                )}
                                <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '4px' }}>
                                    Accepted formats: JPEG, PNG, GIF, WebP, PDF (max 5MB)
                                </div>
                            </FormGroup>
                            <FormGroup>
                                <Label>Nationality <RequiredStar>*</RequiredStar></Label>
                                <Select name="nationality" value={formData.nationality || ''} onChange={handleChange} required>
                                    <option value="">- Select Nationality -</option>
                                <option value="USA">USA</option>
                                <option value="Canada">Canada</option>
                                <option value="Mexico">Mexico</option>
                                <option value="Afghanistan">Afghanistan</option>
                                <option value="Albania">Albania</option>
                                <option value="Algeria">Algeria</option>
                                <option value="Andorra">Andorra</option>
                                <option value="Angola">Angola</option>
                                <option value="Antigua and Barbuda">Antigua and Barbuda</option>
                                <option value="Argentina">Argentina</option>
                                <option value="Armenia">Armenia</option>
                                <option value="Australia">Australia</option>
                                <option value="Austria">Austria</option>
                                <option value="Azerbaijan">Azerbaijan</option>
                                <option value="Bahamas">Bahamas</option>
                                <option value="Bahrain">Bahrain</option>
                                <option value="Bangladesh">Bangladesh</option>
                                <option value="Barbados">Barbados</option>
                                <option value="Belarus">Belarus</option>
                                <option value="Belgium">Belgium</option>
                                <option value="Belize">Belize</option>
                                <option value="Benin">Benin</option>
                                <option value="Bhutan">Bhutan</option>
                                <option value="Bolivia">Bolivia</option>
                                <option value="Bosnia and Herzegovina">Bosnia and Herzegovina</option>
                                <option value="Botswana">Botswana</option>
                                <option value="Brazil">Brazil</option>
                                <option value="Brunei">Brunei</option>
                                <option value="Bulgaria">Bulgaria</option>
                                <option value="Burkina Faso">Burkina Faso</option>
                                <option value="Burundi">Burundi</option>
                                <option value="Cabo Verde">Cabo Verde</option>
                                <option value="Cambodia">Cambodia</option>
                                <option value="Cameroon">Cameroon</option>
                                <option value="Central African Republic">Central African Republic</option>
                                <option value="Chad">Chad</option>
                                <option value="Chile">Chile</option>
                                <option value="China">China</option>
                                <option value="Colombia">Colombia</option>
                                <option value="Comoros">Comoros</option>
                                <option value="Congo, Democratic Republic of the">Congo, Democratic Republic of the</option>
                                <option value="Congo, Republic of the">Congo, Republic of the</option>
                                <option value="Costa Rica">Costa Rica</option>
                                <option value="Cote d'Ivoire">Cote d'Ivoire</option>
                                <option value="Croatia">Croatia</option>
                                <option value="Cuba">Cuba</option>
                                <option value="Cyprus">Cyprus</option>
                                <option value="Czech Republic">Czech Republic</option>
                                <option value="Denmark">Denmark</option>
                                <option value="Djibouti">Djibouti</option>
                                <option value="Dominica">Dominica</option>
                                <option value="Dominican Republic">Dominican Republic</option>
                                <option value="East Timor (Timor-Leste)">East Timor (Timor-Leste)</option>
                                <option value="Ecuador">Ecuador</option>
                                <option value="Egypt">Egypt</option>
                                <option value="El Salvador">El Salvador</option>
                                <option value="Equatorial Guinea">Equatorial Guinea</option>
                                <option value="Eritrea">Eritrea</option>
                                <option value="Estonia">Estonia</option>
                                <option value="Eswatini">Eswatini</option>
                                <option value="Ethiopia">Ethiopia</option>
                                <option value="Fiji">Fiji</option>
                                <option value="Finland">Finland</option>
                                <option value="France">France</option>
                                <option value="Gabon">Gabon</option>
                                <option value="Gambia">Gambia</option>
                                <option value="Georgia">Georgia</option>
                                <option value="Germany">Germany</option>
                                <option value="Ghana">Ghana</option>
                                <option value="Greece">Greece</option>
                                <option value="Grenada">Grenada</option>
                                <option value="Guatemala">Guatemala</option>
                                <option value="Guinea">Guinea</option>
                                <option value="Guinea-Bissau">Guinea-Bissau</option>
                                <option value="Guyana">Guyana</option>
                                <option value="Haiti">Haiti</option>
                                <option value="Honduras">Honduras</option>
                                <option value="Hungary">Hungary</option>
                                <option value="Iceland">Iceland</option>
                                <option value="India">India</option>
                                <option value="Indonesia">Indonesia</option>
                                <option value="Iran">Iran</option>
                                <option value="Iraq">Iraq</option>
                                <option value="Ireland">Ireland</option>
                                <option value="Israel">Israel</option>
                                <option value="Italy">Italy</option>
                                <option value="Jamaica">Jamaica</option>
                                <option value="Japan">Japan</option>
                                <option value="Jordan">Jordan</option>
                                <option value="Kazakhstan">Kazakhstan</option>
                                <option value="Kenya">Kenya</option>
                                <option value="Kiribati">Kiribati</option>
                                <option value="Korea, North">Korea, North</option>
                                <option value="Korea, South">Korea, South</option>
                                <option value="Kosovo">Kosovo</option>
                                <option value="Kuwait">Kuwait</option>
                                <option value="Kyrgyzstan">Kyrgyzstan</option>
                                <option value="Laos">Laos</option>
                                <option value="Latvia">Latvia</option>
                                <option value="Lebanon">Lebanon</option>
                                <option value="Lesotho">Lesotho</option>
                                <option value="Liberia">Liberia</option>
                                <option value="Libya">Libya</option>
                                <option value="Liechtenstein">Liechtenstein</option>
                                <option value="Lithuania">Lithuania</option>
                                <option value="Luxembourg">Luxembourg</option>
                                <option value="Madagascar">Madagascar</option>
                                <option value="Malawi">Malawi</option>
                                <option value="Malaysia">Malaysia</option>
                                <option value="Maldives">Maldives</option>
                                <option value="Mali">Mali</option>
                                <option value="Malta">Malta</option>
                                <option value="Marshall Islands">Marshall Islands</option>
                                <option value="Mauritania">Mauritania</option>
                                <option value="Mauritius">Mauritius</option>
                                <option value="Mexico">Mexico</option>
                                <option value="Micronesia">Micronesia</option>
                                <option value="Moldova">Moldova</option>
                                <option value="Monaco">Monaco</option>
                                <option value="Mongolia">Mongolia</option>
                                <option value="Montenegro">Montenegro</option>
                                <option value="Morocco">Morocco</option>
                                <option value="Mozambique">Mozambique</option>
                                <option value="Myanmar (Burma)">Myanmar (Burma)</option>
                                <option value="Namibia">Namibia</option>
                                <option value="Nauru">Nauru</option>
                                <option value="Nepal">Nepal</option>
                                <option value="Netherlands">Netherlands</option>
                                <option value="New Zealand">New Zealand</option>
                                <option value="Nicaragua">Nicaragua</option>
                                <option value="Niger">Niger</option>
                                <option value="Nigeria">Nigeria</option>
                                <option value="North Macedonia">North Macedonia</option>
                                <option value="Norway">Norway</option>
                                <option value="Oman">Oman</option>
                                <option value="Pakistan">Pakistan</option>
                                <option value="Palau">Palau</option>
                                <option value="Palestine">Palestine</option>
                                <option value="Panama">Panama</option>
                                <option value="Papua New Guinea">Papua New Guinea</option>
                                <option value="Paraguay">Paraguay</option>
                                <option value="Peru">Peru</option>
                                <option value="Philippines">Philippines</option>
                                <option value="Poland">Poland</option>
                                <option value="Portugal">Portugal</option>
                                <option value="Qatar">Qatar</option>
                                <option value="Romania">Romania</option>
                                <option value="Russia">Russia</option>
                                <option value="Rwanda">Rwanda</option>
                                <option value="Saint Kitts and Nevis">Saint Kitts and Nevis</option>
                                <option value="Saint Lucia">Saint Lucia</option>
                                <option value="Saint Vincent and the Grenadines">Saint Vincent and the Grenadines</option>
                                <option value="Samoa">Samoa</option>
                                <option value="San Marino">San Marino</option>
                                <option value="Sao Tome and Principe">Sao Tome and Principe</option>
                                <option value="Saudi Arabia">Saudi Arabia</option>
                                <option value="Senegal">Senegal</option>
                                <option value="Serbia">Serbia</option>
                                <option value="Seychelles">Seychelles</option>
                                <option value="Sierra Leone">Sierra Leone</option>
                                <option value="Singapore">Singapore</option>
                                <option value="Slovakia">Slovakia</option>
                                <option value="Slovenia">Slovenia</option>
                                <option value="Solomon Islands">Solomon Islands</option>
                                <option value="Somalia">Somalia</option>
                                <option value="South Africa">South Africa</option>
                                <option value="South Sudan">South Sudan</option>
                                <option value="Spain">Spain</option>
                                <option value="Sri Lanka">Sri Lanka</option>
                                <option value="Sudan">Sudan</option>
                                <option value="Suriname">Suriname</option>
                                <option value="Sweden">Sweden</option>
                                <option value="Switzerland">Switzerland</option>
                                <option value="Syria">Syria</option>
                                <option value="Taiwan">Taiwan</option>
                                <option value="Tajikistan">Tajikistan</option>
                                <option value="Tanzania">Tanzania</option>
                                <option value="Thailand">Thailand</option>
                                <option value="Togo">Togo</option>
                                <option value="Tonga">Tonga</option>
                                <option value="Trinidad and Tobago">Trinidad and Tobago</option>
                                <option value="Tunisia">Tunisia</option>
                                <option value="Turkey">Turkey</option>
                                <option value="Turkmenistan">Turkmenistan</option>
                                <option value="Tuvalu">Tuvalu</option>
                                <option value="Uganda">Uganda</option>
                                <option value="Ukraine">Ukraine</option>
                                <option value="United Arab Emirates">United Arab Emirates</option>
                                <option value="United Kingdom">United Kingdom</option>
                                <option value="United States">United States</option>
                                <option value="Uruguay">Uruguay</option>
                                <option value="Uzbekistan">Uzbekistan</option>
                                <option value="Vanuatu">Vanuatu</option>
                                <option value="Vatican City">Vatican City</option>
                                <option value="Venezuela">Venezuela</option>
                                <option value="Vietnam">Vietnam</option>
                                <option value="Yemen">Yemen</option>
                                <option value="Zambia">Zambia</option>
                                <option value="Zimbabwe">Zimbabwe</option>
                                </Select>
                                {formErrors.nationality && <span style={{ color: 'red', fontSize: '0.8rem' }}>{formErrors.nationality}</span>}
                            </FormGroup>
                        </div>
                    </FormRow>
                    <FormRow style={{ gridTemplateColumns: '1fr 1fr' }}>
                        <FormGroup>
                            <Label>Social Security Number (Last 4 digits) <RequiredStar>*</RequiredStar></Label>
                            <Input
                                name="socialSecurityNumber"
                                value={formData.socialSecurityNumber}
                                onChange={handleChange}
                                required
                                placeholder="1234"
                                error={!!formErrors.socialSecurityNumber}
                                maxLength="4"
                            />
                            {formErrors.socialSecurityNumber && <span style={{ color: 'red', fontSize: '0.8rem' }}>{formErrors.socialSecurityNumber}</span>}
                        </FormGroup>
                        <FormGroup>
                            <Label>Social Security Card Upload</Label>
                            <FileInput
                                name="socialSecurityUpload"
                                type="file"
                                onChange={handleFileChange}
                                accept=".jpg,.jpeg,.png,.gif,.webp,.pdf"
                            />
                            {formErrors.socialSecurityUpload && (
                                <span style={{ color: 'red', fontSize: '0.8rem' }}>{formErrors.socialSecurityUpload}</span>
                            )}
                            <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '4px' }}>
                                Accepted formats: JPEG, PNG, GIF, WebP, PDF (max 5MB)
                            </div>
                        </FormGroup>
                    </FormRow>

                    <FormRow style={{ gridTemplateColumns: '1fr 1fr' }}>
                        <FormGroup>
                            <Label>DL or State ID Upload</Label>
                            <FileInput
                                name="dlUpload"
                                type="file"
                                onChange={handleFileChange}
                                accept=".jpg,.jpeg,.png,.gif,.webp,.pdf"
                            />
                            {formErrors.dlUpload && (
                                <span style={{ color: 'red', fontSize: '0.8rem' }}>{formErrors.dlUpload}</span>
                            )}
                            <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '4px' }}>
                                Accepted formats: JPEG, PNG, GIF, WebP, PDF (max 5MB)
                            </div>
                        </FormGroup>
                    </FormRow>
                    <FormRow style={{ gridTemplateColumns: '1fr 1fr' }}>
                        <FormGroup>
                            <Label>Emergency Contact Phone</Label>
                            <PhoneInputContainer>
                                <CountryCode>+1</CountryCode>
                                <PhoneInput
                                    name="emergencyContactPhone"
                                    value={formData.emergencyContactPhone || ''}
                                    onChange={handleChange}
                                    placeholder="(123) 456-7890"
                                />
                                <PhoneIcon>📞</PhoneIcon>
                            </PhoneInputContainer>
                        </FormGroup>
                        <FormGroup>
                            <Label>Emergency Contact Name</Label>
                            <Input name="otherEmergencyContact" value={formData.otherEmergencyContact || ''} onChange={handleChange} placeholder="Enter emergency contact name" />
                        </FormGroup>
                    </FormRow>
                </Section>

                {/* Other Details Section */}
                <Section>
                    <SectionTitle>Other Details</SectionTitle>
                    <FormRow style={{ gridTemplateColumns: '1fr 1fr' }}>
                        <FormGroup>
                            <Label>Course <RequiredStar>*</RequiredStar></Label>
                            <CourseSelect
                                value={formData.course}
                                onChange={(value) => setFormData(prev => ({ ...prev, course: value }))}
                                placeholder="Select course"
                                isClearable={false}
                                required
                              style={{padding: '12px'}}

                            />
                            {formErrors.course && <span style={{ color: 'red', fontSize: '0.8rem' }}>{formErrors.course}</span>}
                        </FormGroup>
                        <FormGroup>
                            <Label>Session <RequiredStar>*</RequiredStar></Label>
                            <SessionSelect
                                value={formData.session}
                                onChange={(value) => setFormData(prev => ({ ...prev, session: value }))}
                                placeholder="Select session"
                                isClearable={false}
                                required
                              style={{padding: '12px'}}

                            />
                            {formErrors.session && <span style={{ color: 'red', fontSize: '0.8rem' }}>{formErrors.session}</span>}
                        </FormGroup>
                        <FormGroup style={{ gridColumn: '1 / -1' }}>
                            <Label>Student Notes</Label>
                            {formData.studentNotes.map((note, index) => (
                                <div key={index} style={{ marginBottom: '15px', border: '1px solid #e1e5e9', borderRadius: '8px', padding: '15px' }}>
                                    <TextArea
                                        value={note.note}
                                        onChange={(e) => handleNotesChange(index, 'note', e.target.value)}
                                        placeholder="Add student notes..."
                                        rows="3"
                                        style={{ marginBottom: '10px' }}
                                    />
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '10px' }}>
                                        <Input
                                            value={note.enteredBy}
                                            onChange={(e) => handleNotesChange(index, 'enteredBy', e.target.value)}
                                            placeholder="Entered by"
                                        />
                                        <Input
                                            type="date"
                                            value={note.date}
                                            onChange={(e) => handleNotesChange(index, 'date', e.target.value)}
                                        />
                                        {formData.studentNotes.length > 1 && (
                                            <Button
                                                type="button"
                                                onClick={() => removeNote(index)}
                                                style={{ background: '#dc2626', color: 'white', padding: '8px 12px', fontSize: '14px' }}
                                            >
                                                Remove
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            ))}
                            <Button
                                type="button"
                                onClick={addNote}
                            style={{ width: 'fit-content', background: 'rgb(74, 85, 104)', color: 'white', padding: '8px 15px', fontSize: '14px', marginTop: '10px' }}
                            >
                                + Add Another Note
                            </Button>
                        </FormGroup>
                    </FormRow>
                    <FormRow style={{ gridTemplateColumns: '1fr 1fr' }}>

                        <FormGroup>
                            <Label>Career Coach Name<RequiredStar>*</RequiredStar></Label>
                            <CareerCoachField
                                value={formData.workForceCenter || ''}
                                onChange={(name) => {
                                    setFormData((prev) => ({ ...prev, workForceCenter: name }));
                                    if (formErrors.workForceCenter) {
                                        setFormErrors((prev) => {
                                            const next = { ...prev };
                                            delete next.workForceCenter;
                                            return next;
                                        });
                                    }
                                }}
                                error={formErrors.workForceCenter}
                                required
                            />
                        </FormGroup>
                    </FormRow>
                </Section>

                {/* Course Preference - 1st Choice */}
                <Section>
                    <SectionTitle>Course Preference - 1st Choice</SectionTitle>
                    <FormRow style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
                        <FormGroup>
                            <Label>Course Preference - 1st Choice <RequiredStar>*</RequiredStar></Label>
                            <CourseSelect
                                value={formData.coursePref1}
                                onChange={(value) => setFormData(prev => ({ ...prev, coursePref1: value }))}
                                placeholder="Select course"
                                isClearable={false}
                                required
                              style={{padding: '12px'}}
                            />
                            {formErrors.coursePref1 && <span style={{ color: 'red', fontSize: '0.8rem', }}>{formErrors.coursePref1}</span>}
                        </FormGroup>
                        <FormGroup>
                            <Label>Days Preferred: <RequiredStar>*</RequiredStar></Label>
                            <Select name="daysPref1" value={formData.daysPref1 || ''} onChange={handleChange} required>
                                <option value="">- Select Days -</option>
                                <option value="Weekdays">Weekdays</option>
                                <option value="Weekends">Weekends</option>
                                <option value="Evenings">Evenings</option>
                            </Select>
                            {formErrors.daysPref1 && <span style={{ color: 'red', fontSize: '0.8rem' }}>{formErrors.daysPref1}</span>}
                        </FormGroup>
                        <FormGroup>
                            <Label>Location Preference: <RequiredStar>*</RequiredStar></Label>
                            <Select name="locationPref1" value={formData.locationPref1 || ''} onChange={handleChange} required>
                                <option value="">- Select Location -</option>
                                <option value="Main Campus Thorek">Main Campus Thorek</option>
                                <option value="Evergreen Campus">Evergreen Campus</option>
                            </Select>
                            {formErrors.locationPref1 && <span style={{ color: 'red', fontSize: '0.8rem' }}>{formErrors.locationPref1}</span>}
                        </FormGroup>
                    </FormRow>
                </Section>

                {/* Course Preference - 2nd Choice */}
                <Section>
                    <SectionTitle>Course Preference - 2nd Choice</SectionTitle>
                    <FormRow style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
                        <FormGroup>
                            <Label>Course you are interested in:</Label>
                            <CourseSelect
                                value={formData.coursePref2}
                                onChange={(value) => setFormData(prev => ({ ...prev, coursePref2: value }))}
                                placeholder="Select course"
                                isClearable={true}
                              style={{padding: '12px'}}

                            />
                        </FormGroup>
                        <FormGroup>
                            <Label>Days Preferred:</Label>
                            <Select name="daysPref2" value={formData.daysPref2 || ''} onChange={handleChange}>
                                <option value="">- Select Days -</option>
                                <option value="Weekdays">Weekdays</option>
                                <option value="Weekends">Weekends</option>
                                <option value="Evenings">Evenings</option>
                            </Select>
                        </FormGroup>
                        <FormGroup>
                            <Label>Location Preference:</Label>
                            <Select name="locationPref2" value={formData.locationPref2 || ''} onChange={handleChange}>
                                <option value="">- Select Location -</option>
                                <option value="Main Campus Thorek">Main Campus Thorek</option>
                                <option value="Evergreen Campus">Evergreen Campus</option>
                            </Select>
                        </FormGroup>
                    </FormRow>
                </Section>

                {/* Information Session Details */}
                <Section>
                    <SectionTitle>Information Session Details</SectionTitle>
                    <FormRow style={{ gridTemplateColumns: '1fr 1fr' }}>
                        <FormGroup>

                            <Label>Have you attended an information session?</Label>
                            <div style={{ display: 'flex', gap: 16 }}>
                                <label><input type="radio" name="attendedInfoSession" value={true} checked={formData.attendedInfoSession === true} onChange={(e) => setFormData({ ...formData, attendedInfoSession: true })} /> Yes</label>
                                <label><input type="radio" name="attendedInfoSession" value={false} checked={formData.attendedInfoSession === false} onChange={(e) => setFormData({ ...formData, attendedInfoSession: false })} /> No</label>
                            </div>
                        </FormGroup>
                        <FormGroup>
                            <Label>Where did you fill out this information?</Label>
                            <Select name="infoSessionLocation" value={formData.infoSessionLocation || ''} onChange={handleChange}>
                                <option value="">- Select -</option>
                                <option value="office">In Office</option>
                                <option value="online">On Website</option>
                            </Select>
                        </FormGroup>
                    </FormRow>
                    <FormGroup>
                        <Label>Additional Comments:</Label>
                        <TextArea name="additionalComments" value={formData.additionalComments || ''} onChange={handleChange} placeholder="Enter any additional comments here..." />
                    </FormGroup>
                    <FormGroup>
                        <Label>Student Signature: <RequiredStar>*</RequiredStar></Label>
                        <SignaturePad onSignatureChange={(signature) => setFormData(prev => ({ ...prev, signature }))} />
                    </FormGroup>

                    <FormGroup style={{ display: 'flex', alignItems: 'center' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <input type="checkbox" name="nameCapitalization" checked={formData.nameCapitalization || false} onChange={e => setFormData({ ...formData, nameCapitalization: e.target.checked })} />
                            I have adhered to the above Name Capitalization instructions
                        </label>
                    </FormGroup>
                </Section>



                {/* Student Portal Signup */}
                <Section>
                    <SectionTitle>Student Portal Signup</SectionTitle>
                    <FormRow style={{ gridTemplateColumns: '1fr 1fr' }}>
                        <FormGroup>
                            <Label>Login ID <RequiredStar>*</RequiredStar></Label>
                            <Input
                                name="loginId"
                                value={formData.loginId}
                                onChange={handleChange}
                                required
                                placeholder="Enter login ID (min. 8 characters)"
                                error={!!formErrors.loginId}
                                style={{
                                    borderColor: formErrors.loginId ? 'red' : '#e1e5e9',
                                    borderWidth: formErrors.loginId ? '2px' : '1px'
                                }}
                            />
                            {formErrors.loginId && (
                                <span style={{ color: 'red', fontSize: '0.8rem', marginTop: '4px' }}>
                                    {formErrors.loginId}
                                </span>
                            )}
                            <span style={{ fontSize: '0.8rem', color: '#666', marginTop: '4px' }}>
                                Use only letters and numbers, minimum 8 characters
                            </span>
                        </FormGroup>
                        <FormGroup>
                            <Label>Password <RequiredStar>*</RequiredStar></Label>
                            <Input name="password" type="password" value={formData.password || ''} onChange={handleChange} placeholder="Enter password" required />
                        </FormGroup>
                    </FormRow>
                </Section>

                {/* Update error summary to show when there are either form errors or backend error */}
                {(Object.keys(formErrors).length > 0 || backendError) && (
                    <ErrorSummary id="error-summary">
                        <ErrorTitle>
                            {backendError ? 'Submission Error:' : 'Please fill in the following fields:'}
                        </ErrorTitle>
                        <ErrorList>
                            {getAllErrors().map((error, index) => (
                                <ErrorItem key={index}>{error}</ErrorItem>
                            ))}
                        </ErrorList>
                    </ErrorSummary>
                )}

                <ButtonGroup>
                    <SecondaryButton type="button" onClick={handleReset}>Reset</SecondaryButton>
                    <PrimaryButton type="submit" disabled={!isFormValid}>Submit Application</PrimaryButton>
                </ButtonGroup>
            </Form>
        </Container>
    );
};

export default AddStudentApplication; 